import { ilikeToRegExp } from './getHiddenFields';
import testDateLogicRule from './testDateLogicRule';

const shouldUseDefaultRule = evaluationReports => {
  if (!evaluationReports.length) {
    return false;
  }

  let noConditionalLogic, allLogicFailed, firstDescriptionIsLogicFree;

  noConditionalLogic = evaluationReports.every(
    report => report.reason === Reason.empty_node
  );

  allLogicFailed = evaluationReports.every(report => !report.result);

  firstDescriptionIsLogicFree =
    evaluationReports[0].reason === Reason.empty_node;

  return noConditionalLogic || (allLogicFailed && firstDescriptionIsLogicFree);
};

export const GetFieldDescription = ({ fields = [], rule, values = {} }) => {
  if (JSON.stringify(values) === '{}' || !rule) {
    return '';
  }

  const { condition } = rule;
  const descriptions = JSON.parse(condition);

  const results = descriptions.map(({ conditions, andOr }) =>
    evaluateDescription(conditions, andOr, values, fields)
  );

  if (shouldUseDefaultRule(results)) {
    // set default description
    return descriptions[0].description;
  } else {
    for (let j = 0; j < results.length; j++) {
      const { description } = descriptions[j];
      const result = results[j];
      if (result.result && result.reason === Reason.evaluation_result) {
        // we will take the first description that passed evaluation
        return description;
      }
    }
  }
  return ' ';
};

const GetFieldsDescription = ({
  fields = [],
  rules = [],
  values = {},
  includeId
}) => {
  const descriptionsBlock = {};

  if (JSON.stringify(values) === '{}') {
    return descriptionsBlock;
  }

  const relevantRules = rules.filter(
    rule => rule.action.actionType === 'description'
  );

  for (let i = 0; i < relevantRules.length; i++) {
    const { fieldId, condition } = relevantRules[i];
    const descriptions = JSON.parse(condition);

    descriptionsBlock[fieldId] = ' ';

    const results = descriptions.map(({ conditions, andOr }) =>
      evaluateDescription(conditions, andOr, values, fields)
    );

    if (shouldUseDefaultRule(results)) {
      // set default description
      descriptionsBlock[fieldId] = includeId
        ? { description: descriptions[0].description, id: descriptions[0]._id }
        : descriptions[0].description;
    } else {
      for (let j = 0; j < results.length; j++) {
        const { description, _id } = descriptions[j];
        const result = results[j];
        if (result.result && result.reason === Reason.evaluation_result) {
          // we will take the first description that passed evaluation
          descriptionsBlock[fieldId] = includeId
            ? { description, id: _id }
            : description;
          break;
        }
      }
    }
  }
  return descriptionsBlock;
};

const evaluateDescription = (conditions, andOr, values, fields) => {
  if (!conditions || !conditions.length) {
    // empty condition - we say its evaluated as false
    return report(false, Reason.empty_node);
  }

  const results = conditions.map(condition =>
    evaluateConditions(condition, values, fields)
  );
  const result =
    andOr === 'or'
      ? results.some(report => report.result)
      : results.every(report => report.result);

  const reason = results.every(report => report.reason === Reason.empty_node)
    ? Reason.empty_node
    : Reason.evaluation_result;

  return report(result, reason);
};

const evaluateConditions = (condition, values, fields) => {
  const { comparisons, andOr, input } = condition;

  const userValue = values[input];
  const field = fields.find(f => f.internalId === input);

  if (!comparisons || !comparisons.length || !field) {
    // empty condition - we say its evaluated as false
    return report(false, Reason.empty_node);
  }

  const results = comparisons.map(comparison =>
    evaluateComparison(userValue, comparison, field.uiType)
  );

  const result =
    andOr === 'or'
      ? results.some(report => report.result)
      : results.every(report => report.result);

  const reason = results.every(report => report.reason === Reason.empty_node)
    ? Reason.empty_node
    : Reason.evaluation_result;

  return report(result, reason);
};

const evaluateComparison = (userValue, comparison, type) => {
  const { op, value } = comparison;
  let reason = Reason.evaluation_result;

  if (!op) {
    // empty condition - we say its evaluated as false
    reason = Reason.empty_node;
    return report(false, reason);
  }

  let passed;

  switch (type) {
    case 'date':
      passed = testDateLogicRule(userValue, comparison);
      return report(passed, reason);
    case 'number':
    case 'score':
    case 'currency':
      // empty field always fails
      if (value === '') {
        return report(false, reason);
      }

      switch (op) {
        case '=':
          passed = +userValue === +value;
          break;

        case '>':
          passed = +userValue > +value;
          break;

        case '>=':
          passed = +userValue >= +value;
          break;

        case '<':
          passed = +userValue < +value;
          break;

        case '<=':
          passed = +userValue <= +value;
          break;

        default:
          return report(false, reason);
      }

      return report(passed, reason);

    case 'email_address':
    case 'text':
    case 'long_text':
    case 'multiple_choice':
    case 'lookup':
    case 'user_lookup':
      userValue = String(userValue || '');

      switch (op) {
        case 'ILIKE':
          passed = ilikeToRegExp(value.trim()).test(userValue);
          break;

        case 'NOT ILIKE':
          passed = !ilikeToRegExp(value.trim()).test(userValue);
          break;

        case '=':
          passed = value.trim() === userValue.trim();
          break;

        case '<>':
          passed = value.trim() !== userValue.trim();
          break;

        default:
          return report(false, reason);
      }

      return report(passed, reason);

    case 'confirmation':
      switch (op) {
        case '=':
          passed = value === String(userValue || 'false');
          break;
        case 'IS NULL':
          passed = !!!userValue;
          break;

        default:
          return report(false, reason);
      }
      return report(passed, reason);
    case 'yes_no':
    case 'dropdown':
      switch (op) {
        case '=':
          passed = value === String(userValue || '');
          break;

        case '<>':
          passed = value !== String(userValue || '');
          break;

        case 'IS NULL':
          passed = userValue === null;
          break;

        default:
          return report(false, reason);
      }
      return report(passed, reason);

    default:
      return report(false, reason);
  }
};

const Reason = Object.freeze({
  evaluation_result: 1,
  empty_node: 2
});

const report = (evaluationResult, reason) => {
  return Object.freeze({ result: evaluationResult, reason: reason });
};

export default GetFieldsDescription;
